import { useEffect, useState } from "react";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { MarketTicker } from "@/components/brand/MarketTicker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Plus, BarChart3, Building2, Users, Coffee, Globe, RefreshCw, Undo2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, Cell, XAxis, YAxis, Tooltip, PieChart, Pie } from "recharts";
import { KAFFA } from "@/constants/testIds";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { AdminToursSection } from "@/components/brand/AdminToursSection";
import { SampleManageDialog } from "@/components/brand/SampleManageDialog";

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const { formatPrice, t, tData, formatDate } = useLocale();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [coops, setCoops] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [lots, setLots] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [newCoopOpen, setNewCoopOpen] = useState(false);
  const [coopForm, setCoopForm] = useState({ name: "", cnpj: "", region: "", state: "", country: "Brasil", email: "", phone: "", description: "" });
  const [newCoopUserOpen, setNewCoopUserOpen] = useState(false);
  const [coopUserForm, setCoopUserForm] = useState({ email: "", password: "", name: "", cooperative_id: "", phone: "" });
  const [refreshing, setRefreshing] = useState(false);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, userId: null, reason: "" });
  const [samples, setSamples] = useState([]);
  const [manageSample, setManageSample] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  const refreshFromApi = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/indicators/refresh-from-api");
      const updated = (data?.updated || []).length;
      const errors = (data?.errors || []).length;
      if (updated > 0 && errors === 0) toast.success(t("admin.refresh_done"));
      else if (updated > 0) toast.warning(t("admin.refresh_partial"));
      else toast.error(t("admin.refresh_failed"));
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const reload = async () => {
    try {
      const [s, pu, au, co, ind, lo, pr, ru, sa] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { role: "buyer", status: "pending" } }),
        api.get("/admin/users"),
        api.get("/cooperatives"),
        api.get("/indicators"),
        api.get("/lots"),
        api.get("/proposals"),
        api.get("/admin/users/rejected"),
        api.get("/interests"),
      ]);
      setStats(s.data); setPendingUsers(pu.data); setAllUsers(au.data);
      setCoops(co.data); setIndicators(ind.data); setLots(lo.data); setProposals(pr.data);
      setRejectedUsers(ru.data);
      setSamples(sa.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { if (user && user.role === "admin") reload(); }, [user]);

  if (user === false) return <Navigate to="/login" />;
  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/" />;

  const approve = async (userId, approveIt, reason = null) => {
    try {
      await api.post(`/admin/users/${userId}/approve`, {
        approve: approveIt,
        rejection_reason: approveIt ? null : (reason || "Cadastro rejeitado pelo backoffice"),
      });
      toast.success(approveIt ? t("admin.user_approved") : t("admin.user_rejected"));
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openReject = (userId) => setRejectDialog({ open: true, userId, reason: "" });
  const confirmReject = async () => {
    const { userId, reason } = rejectDialog;
    setRejectDialog({ open: false, userId: null, reason: "" });
    await approve(userId, false, reason);
  };

  const restore = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/restore`);
      toast.success(t("admin.user_restored"));
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createCoop = async () => {
    try {
      await api.post("/cooperatives", coopForm);
      toast.success(t("admin.coop_created")); setNewCoopOpen(false);
      setCoopForm({ name: "", cnpj: "", region: "", state: "", country: "Brasil", email: "", phone: "", description: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createCoopUser = async () => {
    try {
      await api.post("/admin/cooperative-users", coopUserForm);
      toast.success(t("admin.coop_user_created")); setNewCoopUserOpen(false);
      setCoopUserForm({ email: "", password: "", name: "", cooperative_id: "", phone: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveIndicator = async (ind, newVal) => {
    try {
      await api.patch(`/indicators/${ind.code}`, {
        name: ind.name, code: ind.code,
        value: Number(newVal), unit: ind.unit, change_pct: ind.change_pct,
      });
      toast.success(t("admin.indicator_updated")); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!stats) return null;

  const typeData = stats.top_types?.map((tp) => ({ name: tp.type, value: tp.bags, label: tData("coffee", tp.type) })) || [];
  const regionData = stats.top_regions?.map((r) => ({ name: r.region, bags: r.bags, label: tData("region", r.region) })) || [];
  const CHART_COLORS = ["#5e1f0c", "#e9b87a", "#9c6b51", "#d97706", "#7c2d12", "#a16207"];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <MarketTicker />
      <main className="flex-1 mx-auto max-w-[1600px] w-full px-4 sm:px-8 py-10">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3">
          {t("admin.desk")}
        </div>
        <h1 className="font-display text-4xl tracking-tighter font-black">{t("admin.title")}</h1>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border mt-8">
          <Kpi label={t("admin.kpi_active_lots")} value={stats.published_lots} accent />
          <Kpi label={t("admin.kpi_bags")} value={stats.total_bags?.toLocaleString()} />
          <Kpi label={t("admin.kpi_buyers")} value={stats.approved_buyers} />
          <Kpi label={t("admin.kpi_pending")} value={stats.pending_buyers} highlight={stats.pending_buyers > 0} />
          <Kpi label={t("admin.kpi_coops")} value={stats.total_cooperatives} />
          <Kpi label={t("admin.kpi_props")} value={`${stats.accepted_proposals}/${stats.total_proposals}`} />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="border border-border bg-card p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 mb-4">
              <BarChart3 className="h-3.5 w-3.5" /> {t("admin.chart_region")}
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={regionData}>
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" style={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="bags" fill="hsl(var(--accent))" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border border-border bg-card p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 mb-4">
              <Coffee className="h-3.5 w-3.5" /> {t("admin.chart_types")}
            </div>
            <div className="h-72 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeData} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={100} innerRadius={50} stroke="hsl(var(--card))" strokeWidth={2}>
                    {typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              {typeData.map((tp, i) => (
                <div key={tp.name} className="flex items-center gap-2">
                  <span className="h-2 w-2" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-muted-foreground">{tp.label}</span>
                  <span className="ml-auto font-mono-data">{tp.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Tabs defaultValue="approvals" className="mt-8">
          <TabsList className="rounded-none border border-border bg-card h-auto p-0 w-full justify-start flex-wrap">
            <TabsTrigger value="approvals" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_approvals")} · {pendingUsers.length}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_users")} · {allUsers.length}
            </TabsTrigger>
            <TabsTrigger value="cooperatives" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_coops")} · {coops.length}
            </TabsTrigger>
            <TabsTrigger value="lots" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_lots")} · {lots.length}
            </TabsTrigger>
            <TabsTrigger value="proposals" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_proposals")} · {proposals.length}
            </TabsTrigger>
            <TabsTrigger value="samples" data-testid="admin-tab-samples" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_samples")} · {samples.length}
            </TabsTrigger>
            <TabsTrigger value="indicators" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_indicators")} · {indicators.length}
            </TabsTrigger>
            <TabsTrigger value="visits" data-testid="admin-tab-visits" className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("admin.tab_visits")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="approvals" className="mt-6">
            <Tabs defaultValue="pending" className="space-y-4">
              <TabsList className="rounded-none border border-border bg-card h-auto p-0">
                <TabsTrigger
                  value="pending"
                  data-testid="admin-approvals-subtab-pending"
                  className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                >
                  {t("admin.subtab_pending")} · {pendingUsers.length}
                </TabsTrigger>
                <TabsTrigger
                  value="rejected"
                  data-testid="admin-approvals-subtab-rejected"
                  className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                >
                  {t("admin.subtab_rejected")} · {rejectedUsers.length}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                <div className="border border-border bg-card overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left p-3">{t("admin.col_company")}</th>
                        <th className="text-left p-3">{t("auth.cnpj")}</th>
                        <th className="text-left p-3">{t("auth.country")}</th>
                        <th className="text-left p-3">{t("auth.email")}</th>
                        <th className="text-right p-3">{t("common.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.length === 0 ? (
                        <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("admin.no_pending")}</td></tr>
                      ) : pendingUsers.map((u) => (
                        <tr key={u.id} className="border-b border-border/60 hover:bg-secondary/20">
                          <td className="p-3">
                            <div className="font-medium">{u.razao_social}</div>
                            <div className="text-xs text-muted-foreground">{u.commercial_contact}</div>
                          </td>
                          <td className="p-3 font-mono-data text-xs">{u.cnpj}</td>
                          <td className="p-3 flex items-center gap-1.5"><Globe className="h-3 w-3 text-muted-foreground" /> {u.country}</td>
                          <td className="p-3 text-xs text-muted-foreground">{u.email}</td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button data-testid={KAFFA.adminApproveUser} size="sm" onClick={() => approve(u.id, true)} className="h-7 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider">
                                <Check className="h-3 w-3 mr-1" /> {t("admin.approve")}
                              </Button>
                              <Button data-testid={KAFFA.adminRejectUser} size="sm" variant="outline" onClick={() => openReject(u.id)} className="h-7 rounded-none text-xs uppercase tracking-wider">
                                <X className="h-3 w-3 mr-1" /> {t("admin.reject")}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="rejected">
                <div className="border border-border bg-card overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left p-3">{t("admin.col_company")}</th>
                        <th className="text-left p-3">{t("auth.email")}</th>
                        <th className="text-left p-3">{t("auth.cnpj")}</th>
                        <th className="text-left p-3">{t("admin.col_rejection_reason")}</th>
                        <th className="text-left p-3">{t("admin.col_rejected_at")}</th>
                        <th className="text-right p-3">{t("common.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejectedUsers.length === 0 ? (
                        <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("admin.no_rejected")}</td></tr>
                      ) : rejectedUsers.map((u) => (
                        <tr key={u.id} data-testid={`admin-rejected-user-${u.id}`} className="border-b border-border/60 hover:bg-secondary/20">
                          <td className="p-3">
                            <div className="font-medium">{u.razao_social || u.name || "—"}</div>
                            <div className="text-xs text-muted-foreground">{u.country}</div>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{u.email}</td>
                          <td className="p-3 font-mono-data text-xs">{u.cnpj || "—"}</td>
                          <td className="p-3 text-xs text-muted-foreground max-w-[320px]">
                            <div className="whitespace-pre-line">{u.rejection_reason || "—"}</div>
                          </td>
                          <td className="p-3 text-xs text-muted-foreground font-mono-data">
                            {u.rejected_at ? formatDate(u.rejected_at) : "—"}
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => restore(u.id)}
                              data-testid={`admin-restore-user-${u.id}`}
                              className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                            >
                              <Undo2 className="h-3 w-3 mr-1" /> {t("admin.restore")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("admin.col_name")}</th>
                    <th className="text-left p-3">{t("auth.email")}</th>
                    <th className="text-left p-3">{t("admin.col_role")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((u) => (
                    <tr key={u.id} className="border-b border-border/60">
                      <td className="p-3">{u.name}</td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3"><span className="font-mono-data text-xs uppercase">{u.role}</span></td>
                      <td className="p-3"><StatusBadge status={u.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="cooperatives" className="mt-6">
            <div className="flex justify-end gap-2 mb-4">
              <Button onClick={() => setNewCoopUserOpen(true)} variant="outline" className="rounded-none">
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("admin.new_coop_user")}
              </Button>
              <Button
                data-testid={KAFFA.adminCreateCoop}
                onClick={() => setNewCoopOpen(true)}
                className="rounded-none bg-primary hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("admin.new_coop")}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coops.map((c) => (
                <div key={c.id} className="border border-border bg-card p-5 hover-lift">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-accent" />
                    <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{c.region}</div>
                  </div>
                  <h3 className="font-display text-lg tracking-tight mt-2">{c.name}</h3>
                  <div className="text-xs text-muted-foreground mt-1">{c.state} · {c.country}</div>
                  <div className="text-xs text-muted-foreground mt-3 line-clamp-2">{c.description}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.certifications?.slice(0, 3).map((cert) => (
                      <span key={cert} className="px-1.5 py-0.5 text-[10px] font-mono-data uppercase border border-border bg-secondary/30">{cert}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="lots" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("coop.col_lot")}</th>
                    <th className="text-left p-3">{t("lot.cooperative")}</th>
                    <th className="text-right p-3">{t("coop.col_bags")}</th>
                    <th className="text-right p-3">{t("coop.col_price")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => (
                    <tr key={l.id} className="border-b border-border/60">
                      <td className="p-3 truncate max-w-[260px]">{l.name}</td>
                      <td className="p-3 text-xs text-muted-foreground">{l.cooperative_name}</td>
                      <td className="p-3 text-right font-mono-data">{l.bags_quantity}</td>
                      <td className="p-3 text-right font-mono-data text-accent">{formatPrice(l.price_per_bag, l.currency)}</td>
                      <td className="p-3"><StatusBadge status={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="proposals" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("coop.col_lot")}</th>
                    <th className="text-left p-3">{t("coop.col_buyer")}</th>
                    <th className="text-right p-3">{t("coop.col_price")}</th>
                    <th className="text-right p-3">{t("coop.col_bags")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.id} className="border-b border-border/60">
                      <td className="p-3 truncate max-w-[200px]">{p.lot_name}</td>
                      <td className="p-3 text-xs">{p.buyer_name} · <span className="text-muted-foreground">{p.buyer_country}</span></td>
                      <td className="p-3 text-right font-mono-data">{formatPrice(p.proposed_price_per_bag, p.currency)}</td>
                      <td className="p-3 text-right font-mono-data">{p.bags_quantity}</td>
                      <td className="p-3"><StatusBadge status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="samples" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("samples.col_lot")}</th>
                    <th className="text-right p-3">{t("samples.col_bags")}</th>
                    <th className="text-left p-3">{t("samples.col_shipping")}</th>
                    <th className="text-left p-3">{t("samples.col_tracking")}</th>
                    <th className="text-left p-3">{t("samples.col_status")}</th>
                    <th className="text-left p-3">{t("samples.col_date")}</th>
                    <th className="text-right p-3">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {samples.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t("admin.no_samples")}</td></tr>
                  ) : samples.map((i) => (
                    <tr key={i.id} data-testid={`admin-sample-row-${i.id}`} className="border-b border-border/60 hover:bg-secondary/20 align-top">
                      <td className="p-3 text-xs">
                        <div className="font-medium">{i.lot_name || `#${i.lot_id?.slice(-6)}`}</div>
                        {i.buyer_name && <div className="text-muted-foreground">{i.buyer_name} · {i.buyer_country}</div>}
                      </td>
                      <td className="p-3 text-right font-mono-data">{i.bags_quantity}</td>
                      <td className="p-3 text-xs">
                        {i.shipping_address ? (
                          <div className="leading-snug">
                            {i.shipping_recipient && <div className="font-medium">{i.shipping_recipient}</div>}
                            <div className="text-muted-foreground">
                              {i.shipping_address}
                              {i.shipping_city ? `, ${i.shipping_city}` : ""}
                              {i.shipping_state ? ` · ${i.shipping_state}` : ""}
                            </div>
                            <div className="text-muted-foreground">{i.shipping_country} {i.shipping_postal_code ? `· ${i.shipping_postal_code}` : ""}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{t("samples.address_profile")}</span>
                        )}
                      </td>
                      <td className="p-3 text-xs font-mono-data">
                        {i.tracking_number ? (
                          <>
                            <div data-testid={`admin-sample-${i.id}-tracking`}>{i.tracking_number}</div>
                            {i.tracking_carrier && <div className="text-muted-foreground/70 mt-0.5">{i.tracking_carrier}</div>}
                          </>
                        ) : (
                          <span className="text-muted-foreground/60">{t("samples.tracking_pending")}</span>
                        )}
                      </td>
                      <td className="p-3"><StatusBadge status={i.status} /></td>
                      <td className="p-3 text-muted-foreground text-xs">{formatDate(i.created_at)}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-sample-manage-${i.id}`}
                          onClick={() => { setManageSample(i); setManageOpen(true); }}
                          className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                        >
                          {t("samples.manage_btn")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="indicators" className="mt-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <p className="text-xs text-muted-foreground">
                {t("admin.indicators_help")}
              </p>
              <Button
                onClick={refreshFromApi}
                disabled={refreshing}
                data-testid="admin-refresh-indicators"
                variant="outline"
                className="rounded-none h-9 font-mono-data text-xs uppercase tracking-wider"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                {t("admin.refresh_indicators")}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {indicators.map((ind) => <IndicatorCard key={ind.code} ind={ind} onSave={saveIndicator} />)}
            </div>
          </TabsContent>

          <TabsContent value="visits" className="mt-6">
            <AdminToursSection />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <Dialog open={newCoopOpen} onOpenChange={setNewCoopOpen}>
          <DialogContent className="rounded-none sm:max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{t("admin.new_coop")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label={t("admin.col_name")} className="sm:col-span-2"><Input value={coopForm.name} onChange={(e) => setCoopForm({ ...coopForm, name: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.cnpj")}><Input value={coopForm.cnpj} onChange={(e) => setCoopForm({ ...coopForm, cnpj: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("common.region")}><Input value={coopForm.region} onChange={(e) => setCoopForm({ ...coopForm, region: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("coop.field_state")}><Input value={coopForm.state} onChange={(e) => setCoopForm({ ...coopForm, state: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.country")}><Input value={coopForm.country} onChange={(e) => setCoopForm({ ...coopForm, country: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.email")}><Input type="email" value={coopForm.email} onChange={(e) => setCoopForm({ ...coopForm, email: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.phone")}><Input value={coopForm.phone} onChange={(e) => setCoopForm({ ...coopForm, phone: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("admin.coop_descr_label")} className="sm:col-span-2"><Textarea value={coopForm.description} onChange={(e) => setCoopForm({ ...coopForm, description: e.target.value })} className="rounded-none" /></Field>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={() => setNewCoopOpen(false)} variant="ghost" className="rounded-none">{t("common.cancel")}</Button>
              <Button onClick={createCoop} className="rounded-none bg-primary hover:bg-accent hover:text-accent-foreground">{t("common.create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={newCoopUserOpen} onOpenChange={setNewCoopUserOpen}>
          <DialogContent className="rounded-none sm:max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{t("admin.new_coop_user")}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <Field label={t("admin.col_name")}><Input value={coopUserForm.name} onChange={(e) => setCoopUserForm({ ...coopUserForm, name: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.email")}><Input type="email" value={coopUserForm.email} onChange={(e) => setCoopUserForm({ ...coopUserForm, email: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("auth.password")}><Input type="password" value={coopUserForm.password} onChange={(e) => setCoopUserForm({ ...coopUserForm, password: e.target.value })} className="rounded-none" /></Field>
              <Field label={t("lot.cooperative")}>
                <select
                  value={coopUserForm.cooperative_id}
                  onChange={(e) => setCoopUserForm({ ...coopUserForm, cooperative_id: e.target.value })}
                  className="w-full h-10 px-3 border border-border bg-transparent rounded-none text-sm"
                >
                  <option value="">{t("admin.select_coop")}</option>
                  {coops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={() => setNewCoopUserOpen(false)} variant="ghost" className="rounded-none">{t("common.cancel")}</Button>
              <Button onClick={createCoopUser} className="rounded-none bg-primary hover:bg-accent hover:text-accent-foreground">{t("common.create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject reason dialog */}
        <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, userId: null, reason: "" })}>
          <DialogContent className="rounded-none sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">{t("admin.reject_dialog_title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs font-mono-data uppercase tracking-wider text-muted-foreground">
                {t("admin.reject_dialog_reason")} <span className="text-accent">*</span>
              </Label>
              <Textarea
                rows={4}
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                placeholder={t("admin.reject_dialog_placeholder")}
                data-testid="admin-reject-reason-input"
                className="rounded-none"
              />
              <p className="text-[10px] text-muted-foreground">
                {t("admin.reject_dialog_help")}
              </p>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={() => setRejectDialog({ open: false, userId: null, reason: "" })} className="rounded-none">
                {t("common.cancel")}
              </Button>
              <Button
                onClick={confirmReject}
                disabled={!rejectDialog.reason.trim()}
                data-testid="admin-reject-reason-confirm"
                className="rounded-none bg-destructive hover:bg-destructive/90 text-destructive-foreground font-mono-data text-xs uppercase tracking-wider"
              >
                {t("admin.reject")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SampleManageDialog
          key={manageSample?.id || "none"}
          sample={manageSample}
          open={manageOpen}
          onOpenChange={(o) => { setManageOpen(o); if (!o) setManageSample(null); }}
          onSaved={reload}
        />
      </main>
      <Footer />
    </div>
  );
}

function IndicatorCard({ ind, onSave }) {
  const [val, setVal] = useState(String(ind.value));
  const { t, formatDateTime } = useLocale();
  useEffect(() => { setVal(String(ind.value)); }, [ind.value]);
  const isLive = ind.source === "alpha_vantage";
  return (
    <div className={`border bg-card p-5 ${isLive ? "border-accent/40" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{ind.code}</div>
        {isLive && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono-data uppercase tracking-wider border border-accent/40 bg-accent/10 text-accent">
            <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse" />
            {t("admin.live_label")}
          </span>
        )}
      </div>
      <div className="font-display text-lg tracking-tight mt-1">{ind.name}</div>
      <div className="mt-4 flex items-end gap-3">
        <Input value={val} onChange={(e) => setVal(e.target.value)} type="number" step="0.01" className="rounded-none h-10" />
        <div className="text-xs text-muted-foreground font-mono-data shrink-0 mb-2">{ind.unit}</div>
        <Button data-testid={KAFFA.adminIndicatorSave} onClick={() => onSave(ind, val)} className="rounded-none h-10 bg-primary hover:bg-accent hover:text-accent-foreground text-xs uppercase tracking-wider">
          {t("common.save")}
        </Button>
      </div>
      <div className="text-[10px] font-mono-data uppercase tracking-wider mt-3 flex items-center justify-between gap-2">
        <span className={ind.change_pct >= 0 ? "text-emerald-500" : "text-red-500"}>
          {ind.change_pct >= 0 ? "▲" : "▼"} {Math.abs(ind.change_pct).toFixed(2)}%
        </span>
        {ind.updated_at && (
          <span className="text-muted-foreground/70">
            {formatDateTime(ind.updated_at)}
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Kpi({ label, value, accent = false, highlight = false }) {
  return (
    <div className={`bg-card p-5 ${accent ? "border-l-2 border-l-accent" : ""}`}>
      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-2xl sm:text-3xl tracking-tight mt-2 ${highlight ? "text-amber-500" : accent ? "text-accent" : "text-foreground"}`}>{value}</div>
    </div>
  );
}
